import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Users, ArrowRight, XCircle } from 'lucide-react';
import { IMAGES } from '../lib/constants';
interface LandingScreenProps {
  onStart: () => void;
}
export function LandingScreen({
  onStart
}: LandingScreenProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} className="flex flex-col items-center text-center pb-10 min-h-[100dvh] bg-white max-w-md mx-auto shadow-2xl relative overflow-y-auto hide-scrollbar" data-id="element-160">
      {/* Aggressive Warning Banner */}
      <div className="w-full bg-red-600 text-white py-2 px-4 flex items-center justify-center gap-2 shadow-md z-10" data-id="element-161">
        <AlertTriangle className="w-5 h-5 animate-pulse" data-id="element-162" />
        <span className="text-xs font-black uppercase tracking-wider" data-id="element-163">
          ATENCIÓN: Si tienes más de 5 kilos de sobrepeso, lee esto.
        </span>
      </div>

      <div className="px-5 pt-6 w-full flex flex-col items-center" data-id="element-164">
        <img src={IMAGES.logo} alt="Código Metabólico" className="h-12 mb-6 object-contain" data-id="element-165" />

        <h1 className="text-3xl sm:text-4xl font-black text-dark mb-4 leading-[1.1] tracking-tight" data-id="element-166">
          ¿Estás harto de verte al espejo y odiar esa{' '}
          <span className="text-red-600 underline decoration-4 underline-offset-4" data-id="element-167">
            panza que no desaparece?
          </span>
        </h1>

        <p className="text-gray-700 font-medium mb-6 text-base sm:text-lg leading-snug" data-id="element-168">
          No eres tú. Es tu metabolismo que está{' '}
          <strong className="text-dark" data-id="element-169">completamente roto</strong> y en modo
          "almacenar grasa". Descubre por qué todo lo que comes te engorda.
        </p>

        {/* Social proof — international expansion */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-6 w-full text-center shadow-inner" data-id="element-170">
          <p className="text-blue-900 font-black text-sm mb-1 uppercase tracking-wide" data-id="element-171">
            🇲🇽 ¡Por fin llegamos a México!
          </p>
          <p className="text-blue-800 text-xs leading-relaxed font-medium" data-id="element-172">
            Después de más de{' '}
            <span className="font-black text-red-600 bg-red-100 px-1 rounded" data-id="element-173">
              10,000 ventas
            </span>{' '}
            en otros países, ahora está disponible para ti.
          </p>
          <div className="flex items-center justify-center gap-2 text-[10px] text-blue-600 font-bold mt-2 uppercase" data-id="element-174">
            <span data-id="element-175">🇺🇸 USA</span> • <span data-id="element-176">🇧🇷 BR</span> • <span data-id="element-177">🇨🇴 CO</span> •{' '}
            <span data-id="element-178">🇦🇷 AR</span> • <span data-id="element-179">🇪🇸 ES</span>
          </div>
        </div>

        {/* Primary CTA Button */}
        <motion.button whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} onClick={onStart} className="w-full bg-green-600 text-white text-base sm:text-lg font-black py-3.5 rounded-xl shadow-[0_5px_0_rgb(22,163,74)] active:shadow-[0_0px_0_rgb(22,163,74)] active:translate-y-[5px] transition-all mb-6 flex items-center justify-center gap-2 uppercase tracking-tight" data-id="element-180">
          Descubrir mi metabolismo
          <ArrowRight className="w-6 h-6" data-id="element-181" />
        </motion.button>

        {/* Aggressive Pain Section */}
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 mb-6 w-full text-left shadow-lg relative overflow-hidden" data-id="element-182">
          <div className="absolute top-0 right-0 bg-red-600 text-white text-[10px] font-black px-3 py-1 rounded-bl-lg uppercase" data-id="element-183">
            La cruda verdad
          </div>

          <h2 className="text-xl font-black text-red-700 mb-4 mt-2 leading-tight" data-id="element-184">
            Si sigues haciendo lo mismo, seguirás siendo obeso.
          </h2>

          <ul className="space-y-3" data-id="element-185">
            <li className="flex items-start gap-3" data-id="element-186">
              <XCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" data-id="element-187" />
              <span className="text-gray-800 font-bold text-sm" data-id="element-188">
                Estás cansado todo el día y sin energía.
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-189">
              <XCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" data-id="element-190" />
              <span className="text-gray-800 font-bold text-sm" data-id="element-191">
                La ropa te aprieta y te da vergüenza salir.
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-192">
              <XCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" data-id="element-193" />
              <span className="text-gray-800 font-bold text-sm" data-id="element-194">
                Has fracasado en TODAS las dietas que has intentado.
              </span>
            </li>
            <li className="flex items-start gap-3" data-id="element-195">
              <XCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" data-id="element-196" />
              <span className="text-gray-800 font-bold text-sm" data-id="element-197">
                Te sientes frustrado, culpable y sin esperanza.
              </span>
            </li>
          </ul>
        </div>

        <div className="w-full bg-dark text-white rounded-2xl p-5 mb-6 shadow-2xl" data-id="element-198">
          <h3 className="text-xl font-black mb-3 text-yellow-400 uppercase" data-id="element-199">
            El secreto que te ocultan
          </h3>
          <p className="text-sm font-medium leading-relaxed mb-4" data-id="element-200">
            Existen 3 tipos de metabolismo. Si no sabes cuál es el tuyo, estás
            condenado a acumular grasa para siempre. Estás comiendo la dieta de
            otra persona.
          </p>

          {/* Animal images */}
          <div className="flex justify-center gap-3 w-full mt-6" data-id="element-201">
            {[{
            name: 'Jaguar',
            img: IMAGES.jaguar
          }, {
            name: 'Colibrí',
            img: IMAGES.colibri
          }, {
            name: 'Oso',
            img: IMAGES.oso
          }].map((animal, idx) => <motion.div key={animal.name} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.2
          }} className="flex flex-col items-center gap-2 flex-1" data-id="element-202">
                <div className="w-full aspect-square rounded-xl overflow-hidden shadow-lg border-2 border-gray-700 relative bg-gray-800" data-id="element-203">
                  <img src={animal.img} alt={animal.name} className="w-full h-full object-cover opacity-80 mix-blend-screen" data-id="element-204" />
                </div>
                <span className="text-xs font-black text-white uppercase tracking-wider" data-id="element-205">
                  {animal.name}
                </span>
              </motion.div>)}
          </div>
        </div>

        <p className="text-dark font-black text-lg mb-6 px-2 uppercase leading-tight" data-id="element-206">
          ¿Cuál es el tuyo? Descúbrelo en 2 minutos antes de que sea demasiado
          tarde.
        </p>

        <motion.button whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} onClick={onStart} className="w-full bg-red-600 text-white text-lg font-black py-4 rounded-xl shadow-[0_5px_0_rgb(185,28,28)] active:shadow-[0_0px_0_rgb(185,28,28)] active:translate-y-[5px] transition-all flex items-center justify-center gap-2 uppercase tracking-tight" data-id="element-207">
          HACER EL TEST AHORA
          <ArrowRight className="w-6 h-6" data-id="element-208" />
        </motion.button>
      </div>
    </motion.div>;
}